import React, { useEffect, useRef, useState } from 'react';
import { XCircleIcon } from '@heroicons/react/20/solid';

import useAsync from '../hooks/useAsync';
import useModal from '../hooks/useModal';
import useForm from '../hooks/useForm';
import {
	Field,
	FormRow,
	Label,
	TextField,
} from '../components/Form/tailwindForm';
import Slider from '../components/Shell/Slider';
import { Select, SelectCreatable } from '../components/SelectCreatable_old';
import Button from '../components/Button';
import { Input } from '../components/Form';

import { addAssets, getAssets, getLocations } from '../api';
import { toLowerValue } from '../components/helpers/functions';

const AddAssetModal = () => {
	const customSelectRef = useRef();
	const [attrs, setAttrs] = useState([]);
	const [customAttrs, setCustomAttrs] = useState({});
	const { open, setClose } = useModal('addAsset');
	const { form, setItem, reset: resetForm } = useForm();

	const {
		execute: onAdd,
		status,
		error,
		resetError,
	} = useAsync(addAssets, {
		immediate: false,
	});

	const { value: items, execute: refreshAssets } = useAsync(getAssets, {
		immediate: false,
	});

	const { value: locations, execute: refreshLocations } = useAsync(
		getLocations,
		{
			immediate: false,
		}
	);

	useEffect(() => {
		if (open) {
			refreshAssets();
			refreshLocations();
		} else {
			resetForm();
			resetError();
			setAttrs([]);
			setCustomAttrs({});
		}
	}, [open]);

	const uniqueCompanies = [
		...new Map(
			items
				?.map((p) => ({
					label: p.manufacturerName,
					value: toLowerValue(p.manufacturerName),
				}))
				.map((item) => [item['label'], item])
		).values(),
	];

	const models = form?.manufacturerName
		? items
				?.filter((p) => p.manufacturerName === form?.manufacturerName)
				.map((p) => ({
					label: p.modelName,
					value: toLowerValue(p.modelName),
				}))
		: [];

	const uniqueModels = [
		...new Map(models.map((item) => [item['label'], item])).values(),
	];

	const hasError = status === 'error';
	const isLoading = status === 'pending';

	const handleAddAttr = ({ label, key, value }) => {
		setAttrs([...attrs, ...[{ label, key, value }]]);
		setCustomAttrs({});
		customSelectRef.current.focus();
	};

	const attrOptions = [
		{
			label: 'Model Number',
			value: 'modelNumber',
		},
		{
			label: 'Manuf. Date',
			value: 'manufactureDate',
		},
		{
			label: 'Description',
			value: 'description',
		},
		{
			label: 'Category',
			value: 'category',
		},
		{
			label: 'Last Service',
			value: 'lastService',
		},
		{
			label: 'Next Service',
			value: 'nextService',
		},
		{
			label: 'Service Tech',
			value: 'serviceTech',
		},
		{
			label: 'Service Type',
			value: 'serviceType',
		},
		...(customAttrs.label
			? [
					{
						label: customAttrs.label,
						value: customAttrs.key,
					},
			  ]
			: []),
	];

	return (
		<Slider
			name="addAsset"
			title="Add asset"
			description={
				'Add an asset to your inventory by simply filling out the basic information'
			}
			form
			wide
		>
			{hasError ? (
				<div
					className="p-4 mb-4 text-xs text-red-700 bg-red-100 rounded-lg"
					role="alert"
				>
					<span className="font-medium">Uh oh, </span>something went wrong
				</div>
			) : null}
			<FormRow>
				<Label>Manufacturer</Label>
				<Field>
					<SelectCreatable
						options={uniqueCompanies}
						isClearable={true}
						isSearchable={true}
						placeholder="Select or create a manufacturer"
						onChange={(e) => {
							if (!e) {
								setItem('manufacturerName', '');
							} else {
								setItem('manufacturerName', e.label);
							}
						}}
						value={
							form.manufacturerName
								? uniqueCompanies?.find(
										(o) => o.label === form.manufacturerName
								  )
								: null
						}
					/>
				</Field>
			</FormRow>

			<FormRow>
				<Label>Model</Label>
				<Field>
					<SelectCreatable
						placeholder="Select or create a model"
						options={uniqueModels}
						isClearable={true}
						isSearchable={true}
						isDisabled={!form.manufacturerName}
						onChange={(e) => setItem('modelName', e.label)}
						value={
							form?.modelName
								? uniqueModels?.find((o) => o.label === form.modelName)
								: null
						}
					/>
				</Field>
			</FormRow>

			<FormRow>
				<Label>Serial Number</Label>
				<Field>
					<TextField
						placeholder={'SG124132332'}
						onChange={(e) => setItem('serialNumber', e.target.value)}
					/>
				</Field>
			</FormRow>

			<FormRow>
				<Label>Location</Label>
				<Field>
					<Select
						options={locations?.rows?.map((location) => ({
							label: location.name,
							value: location.id,
						}))}
						onChange={(e) => setItem('locationId', e?.value)}
						placeholder="Select a location"
					/>
				</Field>
			</FormRow>

			<FormRow>
				<Label>Custom Attributes</Label>
				<Field className="space-y-2">
					<div className="flex space-x-2 flex-grow">
						<div className="w-full">
							<SelectCreatable
								ref={customSelectRef}
								options={attrOptions}
								isClearable={true}
								isSearchable={true}
								placeholder="Select attribute"
								onChange={(e) => {
									setCustomAttrs({
										...customAttrs,
										label: e?.label || null,
										key: e?.value || null,
									});
								}}
								value={
									attrOptions.find((o) => o.label === customAttrs.label) || ''
								}
								style={{ width: '100%' }}
							/>
						</div>
						<Input
							placeholder="value"
							value={customAttrs.value || ''}
							onChange={(e) =>
								setCustomAttrs({ ...customAttrs, value: e.target.value })
							}
							onKeyDown={(e) => e.key === 'Enter' && handleAddAttr(customAttrs)}
						/>
					</div>

					{attrs?.map((attr) => (
						<div className="flex space-x-2 pt-4">
							<div
								className="flex items-center cursor-pointer"
								onClick={() => {
									setAttrs([...attrs.filter((o) => o.key !== attr.key)]);
								}}
							>
								<XCircleIcon className="w-6 h-6 text-red-400" />
							</div>
							<Input placeholder="label" value={attr.label} disabled />
							<Input placeholder="value" value={attr.value} disabled />
						</div>
					))}
				</Field>
			</FormRow>

			<FormRow className="pt-4" alignRight>
				<Button
					onClick={() =>
						onAdd(
							configurePayload({ ...form, attributes: attrs }),
							(newItem) => {
								setClose();
							}
						)
					}
				>
					{isLoading ? 'Adding...' : 'Add and configure'}
				</Button>
			</FormRow>
		</Slider>
	);
};

const configurePayload = ({
	manufacturerName,
	modelName,
	serialNumber,
	locationId,
	attributes,
} = {}) => ({
	manufacturerName,
	modelName,
	serialNumber,
	locationId,
	attributes,
});

export default AddAssetModal;
